import { GameStage, useJackpot } from '@/context/JackpotProvider'
import { Flex, Box, Text, Spinner } from '@chakra-ui/react'
import { JackpotForm } from './JackpotForm'
import { useTranslation } from 'next-i18next/pages'
import { Image } from '@/components/HOC/Image'
import { SHU_DENOM } from '@/constants/constants'
import JackpotBg from '@/public/assets/images/jackpot-game-bg.png'
import { observer, Observer } from 'mobx-react'
import { computed } from 'mobx'
import { JackpotRollingTrack } from './JackpotRollingTrack'
import { JackpotCounter } from '../molecules/JackpotCounter'
import { RoundNumber } from '@/components/atoms/RoundNumber/RoundNumber'
import { AnimatePresence, motion } from 'framer-motion'
import { FiatOrCryptoAmount } from '@/components/atoms/Amount/Amount'
import { TokenMoney } from '@ninjablaze/sdk-ts'

export const JackpotGamePanel = () => {
  return (
    <Flex gap={3} mb={6} flexDir={{ base: 'column-reverse', md: 'row', lg: 'column-reverse', xl: 'row' }}>
      <Box w={{ base: '100%', md: 72, lg: '100%', xl: 72 }} h={'390px'} flexShrink={0}>
        <JackpotForm />
      </Box>
      <JackpotRolling />
    </Flex>
  )
}

const JackpotRolling = () => {
  const jackpot = useJackpot()
  const state = computed(() => {
    if (jackpot.state.state === GameStage.FINISHED) return GameStage.DRAWING
    if (jackpot.state.state === GameStage.INIT) return GameStage.PROCESSING
    return jackpot.state.state
  })

  return (
    <>
      <Flex flexDir={'column'} h={'390px'} w={'full'}>
        <Box h={'full'} pos={'relative'} rounded={'xl'} alignSelf={'stretch'} overflow={'hidden'}>
          <Image fill={'auto'} objectFit="cover" priority={true} loading="eager" src={JackpotBg} alt={''} />
          <Box pos={'relative'} h={'full'} w={'full'}>
            <JackpotStake />
            <JackpotRoundNumber />
            <Observer>
              {() => (
                <>
                  {state.get() === GameStage.PROCESSING && <JackpotWaiting />}
                  {state.get() === GameStage.DRAWING && <JackpotRollingTrack />}
                </>
              )}
            </Observer>
          </Box>
        </Box>
      </Flex>
    </>
  )
}

const JackpotRoundNumber = observer(() => {
  const jackpot = useJackpot()

  return (
    <Box pos={'absolute'} top={{ base: 4, md: 6 }} right={{ base: 4, md: 6 }}>
      <AnimatePresence>
        {jackpot.state.drawRoundTimestamp && jackpot.state.drawRound && (
          <motion.div key="JackpotRoundNumber" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <RoundNumber
              placement={'bottom-end'}
              roundNumber={jackpot.state.drawRound}
              date={new Date(jackpot.state.drawRoundTimestamp).toLocaleString()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  )
})

const JackpotWaiting = () => {
  const { t } = useTranslation()
  return (
    <Flex justifyContent={'center'}>
      <Flex
        rounded={'3xl'}
        w={'200px'}
        px={6}
        py={4}
        mt={3}
        alignItems={'center'}
        flexDir={'column'}
        border={'1px solid'}
        borderColor={'whiteAlpha.100'}
        justifyContent={'center'}
      >
        <Box mb={1} bg={'#1D1C4912'} p={5} rounded={'3xl'}>
          <Spinner />
        </Box>
        <JackpotTimer />
        <Box fontSize={'sm'} color={'ninja.100'} fontWeight={700} textAlign={'center'}>
          {t('common.waiting-for-other-players')}
        </Box>
      </Flex>
    </Flex>
  )
}

const JackpotTimer = () => {
  const jackpot = useJackpot()
  const { t } = useTranslation()

  return (
    <Observer>
      {() => (
        <>
          {jackpot.state.state === GameStage.INIT ? (
            <Text fontWeight={700} fontSize={'3xl'}>
              {t('tx-status.status-waiting')}
            </Text>
          ) : (
            <JackpotCounter />
          )}
          <Text fontWeight={700}>{t('common.player-count', { count: jackpot.state.players.length })}</Text>
        </>
      )}
    </Observer>
  )
}

const JackpotStake = () => {
  const { t } = useTranslation()

  return (
    <Flex justifyContent={'center'} alignItems={'center'} minW={56} py={4} pt={6} rounded={'xl'}>
      <Box>
        <Text textAlign={'center'} fontSize={'lg'} fontWeight={700} mb={1}>
          {t('Jackpot.stake')}
        </Text>
        <Flex gap={1} alignItems={'center'}>
          <JackpotStakeAmount />
        </Flex>
      </Box>
    </Flex>
  )
}

const JackpotStakeAmount = () => {
  const jackpot = useJackpot()

  return (
    <Observer>
      {() => (
        <FiatOrCryptoAmount
          iconSize={'6'}
          wrapperStyles={{ fontSize: '3xl', fontWeight: 700 }}
          currencyStyles={{
            color: 'ninja.100',
          }}
          value={new TokenMoney(jackpot.state.pool, SHU_DENOM)}
        />
      )}
    </Observer>
  )
}
